import React, { useState, useEffect } from 'react';

// Same document outline, viewBox and size as the DXF / SVG / PDF file icons
// (cloudfront images/*_logo.svg), so the row reads as one set.
const FILE_ICON_FRAME = 'm134.8,7.33c2.93,0,5.86,1.1,7.69,3.3l37.36,37.36c2.2,2.2,3.3,4.76,3.3,7.69v156.78c0,8.06-6.59,14.65-14.65,14.65H21.98c-8.06,0-14.65-6.59-14.65-14.65V21.98c0-8.06,6.59-14.65,14.65-14.65h112.82M134.8,0H21.98C9.89,0,0,9.89,0,21.98v190.48c0,12.09,9.89,21.98,21.98,21.98h146.52c12.09,0,21.98-9.89,21.98-21.98V55.68c0-4.76-1.83-9.52-5.49-12.82L147.62,5.49C143.96,1.83,139.56,0,134.8,0h0Z';

const FileIcon = ({ children }) => (
  <svg className="menu-tab-button-icon" width="41" height="50" viewBox="0 0 190.48 234.44" aria-hidden="true">
    <path className="menu-tab-button-icon-frame" d={FILE_ICON_FRAME} />
    {children}
  </svg>
);

const dielineIconPath = (x, y) => `M${x + 18} ${y} H${x + 36} V${y + 18} H${x + 54} V${y + 36} H${x + 36} V${y + 54} H${x + 18} V${y + 36} H${x} V${y + 18} H${x + 18} Z`;

// MenuTabs is a separate React root, so these buttons read its rendered tabs and
// forward the click to them: visibility (per-drawing flags, custom material), the
// login gate and the redraw-validated navigation stay the tab's own. The buttons
// carry a call-to-action label instead of the tab's name.
const MENU_TABS = [
  {
    menuTab: '3d',
    elementId: 'menu_tab_3d',
    label: () => t('models.create_three_d_mockup'),
    icon: (
      <FileIcon>
        <g stroke="#fff" strokeWidth="3" strokeLinejoin="round">
          <path fill="#f6a383" d="M95 62 L150 92 L95 122 L40 92 Z" />
          <path className="menu-tab-button-icon-brand" d="M40 92 L95 122 V185 L40 155 Z" />
          <path className="menu-tab-button-icon-brand-dark" d="M95 122 L150 92 V155 L95 185 Z" />
        </g>
      </FileIcon>
    ),
  },
  {
    menuTab: 'quote_builder_frank',
    elementId: 'menu_tab_nesting_frank',
    label: () => t('nav.quote_builder_cta'),
    icon: (
      <FileIcon>
        <rect x="50" y="45" width="90" height="152" rx="12" fill="#3c4250" />
        <rect x="62" y="58" width="66" height="30" rx="4" fill="#bfe8d4" />
        <g fill="#fff">
          {[100, 124, 148, 172].map((y) => [62, 86, 110].map((x) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="18" height="16" rx="3" className={x === 110 && y === 172 ? 'menu-tab-button-icon-brand' : undefined} />
          )))}
        </g>
      </FileIcon>
    ),
  },
  {
    menuTab: 'layouts',
    elementId: 'menu_tab_nesting',
    // When the nesting tab also leads to the version-1 quote builder there is no
    // separate Frank tab, so the nesting button names both actions.
    label: (element) => (element.dataset.includesQuoteBuilder === 'true' ? t('models.nest_and_calculate_costs') : t('models.create_nested_layout')),
    icon: (
      <FileIcon>
        <rect x="28" y="60" width="134" height="134" rx="6" fill="#f1f3f6" />
        {[[36, 68], [100, 68], [36, 132], [100, 132]].map(([x, y]) => (
          <g key={`${x}-${y}`}>
            <path d={dielineIconPath(x, y)} fill="#fff" stroke="#ff2116" strokeWidth="4" strokeLinejoin="round" />
            <rect x={x + 18} y={y + 18} width="18" height="18" fill="none" stroke="#2bb34a" strokeWidth="3" />
          </g>
        ))}
      </FileIcon>
    ),
  },
];

const readMenuTabs = () => {
  return MENU_TABS
    .map(({ menuTab, elementId, label, icon }) => {
      const element = document.getElementById(elementId);
      if (!element) { return null; }

      return {
        menuTab,
        elementId,
        label: label(element),
        icon,
        disabled: element.classList.contains('disabledMenuTab'),
      };
    })
    .filter(Boolean);
};

const MenuTabButtons = () => {
  const [menuTabs, setMenuTabs] = useState(readMenuTabs);

  // The tabs change after mount: the Frank tab hides for a custom material, the
  // nesting label follows the combo state and disableMenuTabs() greys them out.
  useEffect(() => {
    const container = document.getElementById('menuTabs');
    if (!container) { return undefined; }

    const observer = new MutationObserver(() => { setMenuTabs(readMenuTabs()); });
    observer.observe(container, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'data-includes-quote-builder'] });
    setMenuTabs(readMenuTabs());
    return () => { observer.disconnect(); };
  }, []);

  const handleClick = (menuTab, elementId) => (e) => {
    e.preventDefault();
    const element = document.getElementById(elementId);
    if (!element) { throw new Error(`MenuTabButtons: #${elementId} is gone, so the click cannot be forwarded`); }

    trackEvent('menu_tab_button_clicked', { menu_tab: menuTab });
    element.click();
  };

  return menuTabs.map(({ menuTab, elementId, label, icon, disabled }) => (
    <div className="inner menu-tab-button-container" key={menuTab}>
      <div className="child">
        <button
          type="button"
          id={`menu_tab_button_${menuTab}`}
          className={`btn btn-default btn-lg download-btn menu-tab-button ${disabled ? 'disabledMenuTab' : ''}`}
          onClick={handleClick(menuTab, elementId)}
        >
          {icon}
          {label}
        </button>
      </div>
    </div>
  ));
};

export default MenuTabButtons;
